// src/components/Speedometer.jsx
import React from 'react';
import { 
  Box, 
  Paper, 
  Typography,
  CircularProgress,
  useTheme 
} from '@mui/material';

const Speedometer = ({ speed, label = "Ball Speed", size = 200 }) => {
  const theme = useTheme();
  
  // Determine speed category and color
  const getSpeedCategory = (speed) => {
    if (speed < 115) return { category: 'SPIN/SLOW', color: theme.palette.info.main };
    if (speed < 125) return { category: 'MEDIUM', color: theme.palette.warning.main };
    if (speed < 140) return { category: 'FAST MEDIUM', color: theme.palette.success.main };
    return { category: 'FAST', color: theme.palette.error.main };
  };
  
  const { category, color } = getSpeedCategory(speed);
  const progress = Math.min((speed / 160) * 100, 100); // Cap at 160 km/h
  
  return (
    <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Typography variant="h6" gutterBottom align="center">
        {label}
      </Typography>
      
      <Box sx={{ position: 'relative', display: 'inline-flex', mt: 2 }}>
        <CircularProgress
          variant="determinate"
          value={progress}
          size={size}
          thickness={4}
          sx={{
            color,
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            }
          }}
        />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
          }}
        >
          <Typography variant="h2" component="div" fontWeight="bold">
            {speed.toFixed(0)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            km/h
          </Typography>
        </Box>
      </Box>
      
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography 
          variant="h6" 
          sx={{ 
            color,
            fontWeight: 'bold',
            mb: 1
          }}
        >
          {category}
        </Typography>
        
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          width: '100%',
          maxWidth: 300,
          mt: 2
        }}>
          <Typography variant="caption" color="text.secondary">
            100
          </Typography>
          <Typography variant="caption" color="text.secondary">
            120
          </Typography>
          <Typography variant="caption" color="text.secondary">
            140
          </Typography>
          <Typography variant="caption" color="text.secondary">
            160+
          </Typography>
        </Box>
        
        <Box sx={{ 
          width: '100%', 
          maxWidth: 300,
          height: 4,
          backgroundColor: theme.palette.grey[300],
          borderRadius: 2,
          mt: 1,
          position: 'relative'
        }}>
          <Box sx={{
            position: 'absolute',
            left: `${(speed - 100) / 60 * 100}%`,
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: color,
            border: `2px solid ${theme.palette.common.white}`,
            boxShadow: 2
          }} />
          
          {/* Speed markers */}
          <Box sx={{ position: 'absolute', left: '0%', top: -8, width: 2, height: 8, backgroundColor: theme.palette.grey[500] }} />
          <Box sx={{ position: 'absolute', left: '33%', top: -8, width: 2, height: 8, backgroundColor: theme.palette.grey[500] }} />
          <Box sx={{ position: 'absolute', left: '66%', top: -8, width: 2, height: 8, backgroundColor: theme.palette.grey[500] }} />
          <Box sx={{ position: 'absolute', left: '100%', top: -8, width: 2, height: 8, backgroundColor: theme.palette.grey[500] }} />
        </Box>
      </Box>
      
      {/* Conversion to mph */}
      <Box sx={{ mt: 3, p: 2, backgroundColor: theme.palette.background.default, borderRadius: 2 }}>
        <Typography variant="body2" align="center">
          <strong>{(speed * 0.621371).toFixed(1)} mph</strong>
          <Typography variant="caption" color="text.secondary" display="block">
            Equivalent in miles per hour
          </Typography>
        </Typography>
      </Box>
    </Paper>
  );
};

export default Speedometer;